<script setup lang="ts">
import * as THREE from 'three'
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
import {
  CameraMode,
  MeasureOverlayPlugin,
  MeasurePlugin,
  presets,
  type ControlMode,
  type GaussianSplatPlugin,
  type GaussianSplatHandle,
  type MeasureService,
  type MeasureType,
  type SpatialBindingChangedEventPayload,
  type SpatialBindingSnapshot,
  type SpatialRepresentationErrorEventPayload,
  type SpatialRepresentationEventPayload,
  type SpatialRepresentationRole,
  type SpatialRepresentationState,
  type SpatialWorldHandle,
  type ViewPreset,
} from '@sharefe/share3d-engine'
import { getDefaultPresetModelUrl, PRESET_MODELS } from '../examples/presetModels'
import { useExampleGraphicsCard } from '../composables/useExampleGraphicsCard'
import { toGraphicsRuntimeError } from '../utils/graphicsRuntimeError'
import { use3DGS } from '../hooks/use3DGS'
import {
  configureGsplatPlugins,
  getGsplatGraphicsConfig,
  resolveGsplatBackendControlState,
} from '../utils/gsplatBackendControl'
import {
  applySpaceMouseNavigationProfile,
  applySpaceMouseSecondaryControlSelection,
  buildSpaceMouseSecondaryControlGroups,
  createSpaceMouseNavigationState,
  readSpaceMouseNavigationState,
  type SpaceMouseControllerLike,
  type SpaceMouseNavigationProfile,
  type SpaceMouseNavigationState,
} from '../utils/spaceMouseNavigation'
import SceneAssetLoaderCard from './SceneAssetLoaderCard.vue'
import GSplatBackendControlCard from './GSplatBackendControlCard.vue'
import GraphicsBackendControlCard from './GraphicsBackendControlCard.vue'
import SceneControlPanel from './SceneControlPanel.vue'
import SceneGroupCard from './SceneGroupCard.vue'
import SceneMeasureCard from './SceneMeasureCard.vue'
import SceneSpatialQueryCard from './SceneSpatialQueryCard.vue'
import SceneStatusCard from './SceneStatusCard.vue'
import SceneViewCard from './SceneViewCard.vue'

type SpatialEventItem = {
  id: string
  message: string
}

const SPATIAL_ROLES: SpatialRepresentationRole[] = [
  'surface',
  'collision',
  'navigation',
]

const SPACEMOUSE_DEFAULT_PROFILE: SpaceMouseNavigationProfile = 'mouse-only'

const MEASURE_OPTIONS: Array<{
  value: MeasureType
  label: string
  detail: string
}> = [
  { value: 'coordinate', label: '坐标', detail: '单点完成，适合快速取点。' },
  { value: 'distance', label: '距离', detail: '实时预览，右键结束。' },
  { value: 'height', label: '高度', detail: '两点自动完成。' },
  { value: 'circle', label: '圆', detail: '三点拟合圆。' },
  { value: 'azimuth', label: '方位角', detail: '两点自动完成。' },
  { value: 'angle', label: '角度', detail: '建议三点完成，结果更稳定。' },
  { value: 'area', label: '面积', detail: '至少三点，右键结束。' },
]

const VIEW_OPTIONS: Array<{
  value: ViewPreset
  label: string
}> = [
  { value: 'top', label: '俯视' },
  { value: 'bottom', label: '仰视' },
  { value: 'front', label: '前视' },
  { value: 'back', label: '后视' },
  { value: 'left', label: '左视' },
  { value: 'right', label: '右视' },
]

const CONTROL_MODE_OPTIONS: Array<{
  value: ControlMode
  label: string
  detail: string
}> = [
  {
    value: 'orbit',
    label: '轨道',
    detail: '通用观察模式，用于检查代理表面对齐。',
  },
  {
    value: 'earth',
    label: '地球',
    detail: '验证旋转 pivot 是否优先落在空间表面。',
  },
  {
    value: 'spacemouse',
    label: 'SpaceMouse',
    detail: '验证 look-at 复用是否走同一条表面查询路径。',
  },
  {
    value: 'legacy-first-person',
    label: '第一人称（旧版）',
    detail: '验证 focus 与表面解析是否保持一致。',
  },
]

// 默认加载预设 model1
const DEFAULT_GS3D_MODEL = getDefaultPresetModelUrl()

const containerRef = useTemplateRef('containerRef')
const backendControlState = resolveGsplatBackendControlState(window.location.hash, {
  webgpuSupported: true,
})

const graphicsCard = useExampleGraphicsCard({
  presetKey: 'pointCloudAndGSplat',
  extras: ['SpatialQueryPlugin'],
})
const plugins = [
  ...configureGsplatPlugins(presets.pointCloudAndGSplat(), backendControlState),
  new MeasurePlugin(),
  new MeasureOverlayPlugin(),
]

const {
  engine,
  isLoading,
  modelInput,
  stats,
  loadedHandles,
  init,
  loadModel,
  removeAllModels,
  autoLoad,
} = use3DGS(containerRef, {
  plugins,
  defaultModelUrl: DEFAULT_GS3D_MODEL,
  autoLoadFromUrl: true,
  graphics: getGsplatGraphicsConfig(backendControlState),
  fov: 65,
  background: '#020617',
})

const proxyInput = ref('')
const loadError = ref('')
const worldState = ref<'idle' | 'building' | 'ready' | 'error'>('idle')
const worldId = ref('')
const bindingId = ref('')
const bindingDurationMs = ref<number | null>(null)
const bindingDurationPending = ref(false)
const debugVisible = ref(false)
const bvhVisible = ref(false)
const bvhDepth = ref(10)
const gsVisible = ref(true)
const controlMode = ref<ControlMode>('earth')
const cameraMode = ref<CameraMode>(CameraMode.Perspective)
const spaceMouseNavigation = ref<SpaceMouseNavigationState>(
  createSpaceMouseNavigationState(SPACEMOUSE_DEFAULT_PROFILE),
)
const measureType = ref<MeasureType>('distance')
const measureCount = ref(0)
const activeTool = ref<string | null>(null)
const eventLog = ref<SpatialEventItem[]>([])

let currentWorldHandle: SpatialWorldHandle | null = null
const currentBindings = ref<SpatialBindingSnapshot[]>([])
const cleanupTasks: Array<() => void> = []
let bindingDurationStartAt = 0
let bindingDurationWorldId: string | null = null
let spaceMouseDefaultApplied = false

const activeToolLabel = computed(() => {
  if (!activeTool.value) {
    return '未启用'
  }

  return activeTool.value === 'measure' ? '测量' : activeTool.value
})

const cameraModeLabel = computed(() => {
  return cameraMode.value === CameraMode.Perspective ? '透视' : '正交'
})

const modelLoaderHint = computed(() => {
  if (loadError.value) {
    return `加载失败：${loadError.value}`
  }

  return '先加载一份或多份 3DGS 资源，再绑定一份代理网格，在同一个空间世界中构建表面、碰撞和导航三种表示。'
})

const activeMeasureDetail = computed(() => {
  return MEASURE_OPTIONS.find((item) => item.value === measureType.value) ?? null
})

const displayWorldState = computed(() => {
  return formatWorldStateLabel(worldState.value)
})

const spatialBadge = computed(() => {
  if (!currentWorldHandle) {
    return '表面/碰撞/导航'
  }

  return displayWorldState.value
})

const canClearSpatial = computed(() => {
  return !!currentWorldHandle || currentBindings.value.length > 0
})

const bindingDurationLabel = computed(() => {
  if (bindingDurationPending.value) {
    return '记录中...'
  }

  if (bindingDurationMs.value === null) {
    return '暂无记录'
  }

  return formatDuration(bindingDurationMs.value)
})

const measureNote = computed(() => {
  return '测量工具优先命中测量表面，相机控制器优先命中相机控制表面。可先加载多份 3DGS，再验证同一个空间世界绑定多个对象的行为。'
})

const statusItems = computed(() => {
  return [
    { label: '当前工具', value: activeToolLabel.value },
    { label: '相机模式', value: cameraModeLabel.value },
    {
      label: '控制模式',
      value:
        CONTROL_MODE_OPTIONS.find((item) => item.value === controlMode.value)?.label ??
        controlMode.value,
    },
    { label: '空间世界', value: displayWorldState.value },
    { label: '绑定对象', value: currentBindings.value.length },
    { label: '已加载 3DGS', value: stats.value.totalModels },
    { label: '测量结果', value: measureCount.value },
  ]
})

function appendLog(message: string) {
  eventLog.value = [
    {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      message,
    },
    ...eventLog.value,
  ].slice(0, 8)
}

function getMeasureService(): MeasureService | null {
  const engineInstance = engine.value
  if (!engineInstance || !engineInstance.context.hasService('MeasureService')) {
    return null
  }

  return engineInstance.context.getService('MeasureService')
}

function getSpaceMouseController(): SpaceMouseControllerLike | null {
  const controller = engine.value?.controls.getController('spacemouse') as
    | SpaceMouseControllerLike
    | undefined

  return controller ?? null
}

function getSpatialOwners(): GaussianSplatHandle[] {
  return loadedHandles.value
}

function syncGaussianVisibility() {
  for (const handle of loadedHandles.value) {
    if (handle.object3D) {
      handle.object3D.visible = gsVisible.value
    }
  }
}

function deriveWorldState(
  states: SpatialRepresentationState[],
): 'idle' | 'building' | 'ready' | 'error' {
  if (states.length === 0 || states.every((state) => state === 'absent')) {
    return 'idle'
  }

  if (states.some((state) => state === 'error')) {
    return 'error'
  }

  if (states.some((state) => state === 'building')) {
    return 'building'
  }

  if (states.some((state) => state === 'ready')) {
    return 'ready'
  }

  return 'idle'
}

function syncBindingState() {
  const engineInstance = engine.value
  const activeWorldId = currentWorldHandle?.id
  if (!engineInstance || !activeWorldId) {
    currentBindings.value = []
    bindingId.value = ''
    return
  }

  currentBindings.value = engineInstance.spatial.getBindings({
    worldId: activeWorldId,
    includeInactive: true,
  })
  bindingId.value = currentBindings.value.map((binding) => binding.id).join(', ')
}

function applySpatialDebugState() {
  const engineInstance = engine.value
  const activeWorldId = currentWorldHandle?.id
  if (!engineInstance || !activeWorldId) {
    return
  }

  for (const role of SPATIAL_ROLES) {
    engineInstance.spatial.setDebugMeshVisible(activeWorldId, role, debugVisible.value)
    engineInstance.spatial.setDebugBoundsVisible(activeWorldId, role, bvhVisible.value)
    engineInstance.spatial.setDebugBoundsDepth(activeWorldId, role, bvhDepth.value)
  }
}

function startBindingDurationTracking(worldId: string, startedAt = performance.now()) {
  bindingDurationWorldId = worldId
  bindingDurationStartAt = startedAt
  bindingDurationMs.value = null
  bindingDurationPending.value = true
}

function cancelBindingDurationTracking() {
  bindingDurationWorldId = null
  bindingDurationStartAt = 0
  bindingDurationPending.value = false
}

function syncBindingDuration() {
  if (
    !bindingDurationPending.value ||
    !bindingDurationWorldId ||
    !currentWorldHandle ||
    currentWorldHandle.id !== bindingDurationWorldId
  ) {
    return
  }

  const snapshot = currentWorldHandle.snapshot()
  const states = SPATIAL_ROLES.map((role) => snapshot.representations[role].state)
  if (states.some((state) => state === 'building')) {
    return
  }

  const durationMs = performance.now() - bindingDurationStartAt
  const hasError = states.some((state) => state === 'error')
  bindingDurationMs.value = durationMs
  cancelBindingDurationTracking()
  appendLog(`${hasError ? '绑定结束（含错误）' : '绑定完成'}，用时：${formatDuration(durationMs)}`)
}

function syncPanelState() {
  const engineInstance = engine.value
  if (!engineInstance) {
    activeTool.value = null
    measureCount.value = 0
    cameraMode.value = CameraMode.Perspective
    controlMode.value = 'earth'
    spaceMouseNavigation.value = createSpaceMouseNavigationState(SPACEMOUSE_DEFAULT_PROFILE)
    return
  }

  activeTool.value = engineInstance.activeTool
  cameraMode.value = engineInstance.camera.mode
  controlMode.value = engineInstance.controls.activeMode
  spaceMouseNavigation.value = readSpaceMouseNavigationState(
    getSpaceMouseController(),
    SPACEMOUSE_DEFAULT_PROFILE,
  )

  const measureService = getMeasureService()
  if (measureService) {
    measureType.value = measureService.getMeasureType()
    measureCount.value = measureService.getResults().length
  } else {
    measureCount.value = 0
  }
}

function syncSpatialState() {
  if (!currentWorldHandle) {
    worldState.value = 'idle'
    worldId.value = ''
    syncBindingState()
    return
  }

  try {
    const snapshot = currentWorldHandle.snapshot()
    worldState.value = deriveWorldState(
      SPATIAL_ROLES.map((role) => snapshot.representations[role].state),
    )
    worldId.value = snapshot.id
  } catch {
    currentWorldHandle = null
    worldState.value = 'idle'
    worldId.value = ''
  }

  syncBindingState()
  syncBindingDuration()
}

function teardownBindings() {
  while (cleanupTasks.length > 0) {
    cleanupTasks.pop()?.()
  }
}

function bindEngineEvents() {
  const engineInstance = engine.value
  if (!engineInstance) {
    return
  }

  teardownBindings()
  if (!spaceMouseDefaultApplied) {
    const controller = getSpaceMouseController()
    if (controller) {
      applySpaceMouseNavigationProfile(controller, SPACEMOUSE_DEFAULT_PROFILE)
      spaceMouseDefaultApplied = true
    }
  }

  const onSpatialRepresentationReady = (
    event: SpatialRepresentationEventPayload,
  ) => {
    if (event.worldId !== currentWorldHandle?.id) {
      return
    }

    syncSpatialState()
    applySpatialDebugState()
    appendLog(`表示已就绪：${formatRepresentationRoleLabel(event.representationRole)}`)
  }

  const onSpatialRepresentationError = (
    event: SpatialRepresentationErrorEventPayload,
  ) => {
    if (event.worldId !== currentWorldHandle?.id) {
      return
    }

    syncSpatialState()
    loadError.value = event.error.message
    appendLog(`表示构建失败：${formatRepresentationRoleLabel(event.representationRole)}`)
  }

  const onSpatialDisposed = (event: { worldId: string }) => {
    if (event.worldId !== currentWorldHandle?.id) {
      return
    }

    cancelBindingDurationTracking()
    currentWorldHandle = null
    currentBindings.value = []
    bindingId.value = ''
    syncSpatialState()
    appendLog(`世界已释放：${event.worldId}`)
  }

  const onSpatialBindingChanged = (
    event: SpatialBindingChangedEventPayload,
  ) => {
    if (event.binding.worldId !== currentWorldHandle?.id) {
      return
    }

    syncBindingState()
    appendLog(`${formatBindingActionLabel(event.action)}：${event.binding.id}`)
  }

  const handleChange = () => {
    syncPanelState()
  }

  engineInstance.events.on('spatial.representationReady', onSpatialRepresentationReady)
  engineInstance.events.on('spatial.representationError', onSpatialRepresentationError)
  engineInstance.events.on('spatial.worldDisposed', onSpatialDisposed)
  engineInstance.events.on('spatial.bindingChanged', onSpatialBindingChanged)
  engineInstance.events.on('tool.activated', handleChange)
  engineInstance.events.on('tool.deactivated', handleChange)
  engineInstance.events.on('tool.resultChanged', handleChange)
  engineInstance.events.on('camera.modeChanged', handleChange)
  engineInstance.events.on('controls.modeChanged', handleChange)
  engineInstance.events.on('controls.spacemouseNavigationChanged', handleChange)

  cleanupTasks.push(() => engineInstance.events.off('spatial.representationReady', onSpatialRepresentationReady))
  cleanupTasks.push(() => engineInstance.events.off('spatial.representationError', onSpatialRepresentationError))
  cleanupTasks.push(() => engineInstance.events.off('spatial.worldDisposed', onSpatialDisposed))
  cleanupTasks.push(() => engineInstance.events.off('spatial.bindingChanged', onSpatialBindingChanged))
  cleanupTasks.push(() => engineInstance.events.off('tool.activated', handleChange))
  cleanupTasks.push(() => engineInstance.events.off('tool.deactivated', handleChange))
  cleanupTasks.push(() => engineInstance.events.off('tool.resultChanged', handleChange))
  cleanupTasks.push(() => engineInstance.events.off('camera.modeChanged', handleChange))
  cleanupTasks.push(() => engineInstance.events.off('controls.modeChanged', handleChange))
  cleanupTasks.push(() =>
    engineInstance.events.off('controls.spacemouseNavigationChanged', handleChange),
  )
}

function clearProxyBinding() {
  cancelBindingDurationTracking()
  currentBindings.value = []
  bindingId.value = ''

  if (currentWorldHandle) {
    currentWorldHandle.dispose()
    currentWorldHandle = null
  }

  syncSpatialState()
}

function setInitialLoadedCameraView() {
  const engineInstance = engine.value
  if (!engineInstance) {
    return
  }

  engineInstance.camera.setState({
    ...engineInstance.camera.getState(),
    mode: CameraMode.Perspective,
    position: new THREE.Vector3(0, 0, 2),
    target: new THREE.Vector3(0, 1, 2),
    up: new THREE.Vector3(0, 0, 1),
  })
}

function waitForEngineFrames(frameCount = 2, timeoutMs = 500): Promise<void> {
  const engineInstance = engine.value
  if (!engineInstance || frameCount <= 0) {
    return Promise.resolve()
  }
  const events = engineInstance.events

  return new Promise((resolve) => {
    let remainingFrames = frameCount
    let settled = false
    const timeoutId = window.setTimeout(cleanup, timeoutMs)

    function cleanup() {
      if (settled) {
        return
      }
      settled = true
      window.clearTimeout(timeoutId)
      events.off('frame.end', handleFrameEnd)
      resolve()
    }

    function handleFrameEnd() {
      remainingFrames -= 1
      if (remainingFrames <= 0) {
        cleanup()
      }
    }

    events.on('frame.end', handleFrameEnd)
  })
}

async function handleLoadModel() {
  loadError.value = ''

  try {
    if (stats.value.totalModels > 0) {
      clearScene()
    }

    await loadModel()
    await waitForEngineFrames()
    setInitialLoadedCameraView()
    syncGaussianVisibility()
    syncPanelState()
    appendLog('3DGS 加载完成')
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : '未知加载错误'
  }
}

async function handleLoadProxyMesh() {
  loadError.value = ''

  const engineInstance = engine.value
  const owners = getSpatialOwners()
  const proxyUrl = proxyInput.value.trim()

  if (!engineInstance || owners.length === 0) {
    loadError.value = '请先加载至少一份 3DGS 资源，再绑定代理网格。'
    return
  }

  if (!proxyUrl) {
    loadError.value = '请输入代理网格 URL，或从本地目录选择。'
    return
  }

  clearProxyBinding()

  try {
    const bindingStartAt = performance.now()
    currentWorldHandle = engineInstance.spatial.loadWorld({
      name: '共享空间世界',
      representations: {
        surface: {
          source: proxyUrl,
          debug: {
            meshVisible: debugVisible.value,
            boundsVisible: bvhVisible.value,
            boundsDepth: bvhDepth.value,
          },
        },
        collision: {
          source: proxyUrl,
          debug: {
            meshVisible: debugVisible.value,
            boundsVisible: bvhVisible.value,
            boundsDepth: bvhDepth.value,
          },
        },
        navigation: {
          source: proxyUrl,
          debug: {
            meshVisible: debugVisible.value,
            boundsVisible: bvhVisible.value,
            boundsDepth: bvhDepth.value,
          },
        },
      },
    })
    startBindingDurationTracking(currentWorldHandle.id, bindingStartAt)

    currentBindings.value = owners.map((handle, index) =>
      engineInstance.spatial.bindWorld({
        worldId: currentWorldHandle!.id,
        ownerAssetId: handle.id,
        ownerKind: 'gaussian-splat',
        representationRoles: [...SPATIAL_ROLES],
        surfaceUsages: ['measurement', 'camera-control'],
        priority: index,
      }),
    )

    syncSpatialState()
    appendLog(`已请求构建世界：${currentWorldHandle.id}`)
    appendLog(`已创建绑定：${currentBindings.value.length} 个`)
  } catch (error) {
    loadError.value = error instanceof Error ? error.message : '代理网格绑定失败'
  }
}

function clearScene() {
  clearProxyBinding()

  const plugin = engine.value?.plugins.get<GaussianSplatPlugin>('gaussian-splat')
  for (const handle of plugin?.getAll() ?? []) {
    plugin?.unmount(handle.id)
    plugin?.dispose(handle.id)
  }

  removeAllModels(false)
  getMeasureService()?.clearResults()
  loadError.value = ''
  syncPanelState()
  appendLog('场景已清空')
}

function activateMeasureTool(type: MeasureType) {
  const measureService = getMeasureService()
  measureService?.setMeasureType(type)
  engine.value?.activateTool('measure')
  syncPanelState()
}

function handleMeasureSelection(value: string) {
  activateMeasureTool(value as MeasureType)
}

function clearMeasureResults() {
  getMeasureService()?.clearResults()
  syncPanelState()
}

function deactivateTool() {
  engine.value?.deactivateTool()
  syncPanelState()
}

function requiresPerspectiveControlMode(mode: ControlMode): boolean {
  return mode === 'legacy-first-person'
}

function toggleCameraMode() {
  const engineInstance = engine.value
  if (!engineInstance) {
    return
  }

  const nextMode =
    cameraMode.value === CameraMode.Perspective ?
      CameraMode.Orthographic :
      CameraMode.Perspective

  if (
    nextMode === CameraMode.Orthographic &&
    requiresPerspectiveControlMode(controlMode.value)
  ) {
    engineInstance.controls.setMode('orbit')
  }

  engineInstance.camera.setMode(nextMode)
  syncPanelState()
}

function setView(preset: ViewPreset) {
  engine.value?.camera.setPresetView(preset)
}

function handleViewSelection(value: string) {
  setView(value as ViewPreset)
}

function fitAll() {
  engine.value?.fitAll()
}

function setControlMode(mode: ControlMode) {
  if (!engine.value) {
    return
  }

  if (
    requiresPerspectiveControlMode(mode) &&
    engine.value.camera.mode !== CameraMode.Perspective
  ) {
    engine.value.camera.setMode(CameraMode.Perspective)
  }

  engine.value.controls.setMode(mode)
  syncPanelState()
}

function handleControlModeSelection(value: string) {
  setControlMode(value as ControlMode)
}

function handleSpaceMouseSecondaryControlGroupSelection(payload: {
  groupId: string
  value: string
}) {
  applySpaceMouseSecondaryControlSelection(
    getSpaceMouseController(),
    payload.groupId,
    payload.value,
  )
  spaceMouseNavigation.value = readSpaceMouseNavigationState(
    getSpaceMouseController(),
    SPACEMOUSE_DEFAULT_PROFILE,
  )
}

function formatWorldStateLabel(state: 'idle' | 'building' | 'ready' | 'error'): string {
  switch (state) {
    case 'idle':
      return '未创建'
    case 'building':
      return '构建中'
    case 'ready':
      return '就绪'
    case 'error':
      return '错误'
    default:
      return state
  }
}

function formatRepresentationRoleLabel(role: SpatialRepresentationRole): string {
  switch (role) {
    case 'surface':
      return '表面'
    case 'collision':
      return '碰撞'
    case 'navigation':
      return '导航'
    default:
      return role
  }
}

function formatBindingActionLabel(
  action: SpatialBindingChangedEventPayload['action'],
): string {
  switch (action) {
    case 'bound':
      return '已绑定'
    case 'updated':
      return '已更新'
    case 'unbound':
      return '已解绑'
    default:
      return action
  }
}

function formatDuration(durationMs: number): string {
  if (durationMs >= 1000) {
    return `${(durationMs / 1000).toFixed(2)} s`
  }

  if (durationMs >= 100) {
    return `${durationMs.toFixed(1)} ms`
  }

  return `${durationMs.toFixed(2)} ms`
}

watch(debugVisible, () => {
  applySpatialDebugState()
})

watch(bvhVisible, () => {
  applySpatialDebugState()
})

watch(bvhDepth, () => {
  applySpatialDebugState()
})

watch(gsVisible, () => {
  syncGaussianVisibility()
})

watch(loadedHandles, () => {
  syncGaussianVisibility()
  syncBindingState()
})

onMounted(async () => {
  graphicsCard.markCreating()
  try {
    await init()
  } catch (error) {
    graphicsCard.markError(toGraphicsRuntimeError(error))
    return
  }
  graphicsCard.markReady(engine.value ? engine.value.getGraphicsInfo().kind : null)
  bindEngineEvents()
  engine.value?.controls.setMode('earth')
  modelInput.value ||= DEFAULT_GS3D_MODEL
  await autoLoad()
  if (loadedHandles.value.length > 0) {
    await waitForEngineFrames()
    setInitialLoadedCameraView()
  }
  syncGaussianVisibility()
  syncPanelState()
  syncSpatialState()
})

onBeforeUnmount(() => {
  teardownBindings()
  clearProxyBinding()
})
</script>

<template>
  <div class="gs3d-page">
    <div class="render-container" ref="containerRef"></div>

    <SceneControlPanel
      layout="sidebar"
      kicker="空间运行时"
      title="3DGS 空间查询"
      :meta="`${activeToolLabel} / ${cameraModeLabel}`"
    >
      <SceneAssetLoaderCard
        v-model="modelInput"
        title="3DGS 资源"
        subtitle="先加载一份或多份高斯点场景，再绑定共享的空间世界。"
        badge="3DGS"
        :hint="modelLoaderHint"
        :loading="isLoading"
        :can-clear="stats.totalModels > 0"
        :directory-entry-names="['lod-meta.json']"
        :preset-models="PRESET_MODELS"
        directory-pick-label="选择 3DGS 目录"
        load-label="加载 3DGS"
        clear-label="清空场景"
        @submit-load="handleLoadModel()"
        @clear-scene="clearScene()"
      />

      <GraphicsBackendControlCard :card="graphicsCard">
        <template #extensions>
          <GSplatBackendControlCard :state="backendControlState" />
        </template>
      </GraphicsBackendControlCard>

      <SceneSpatialQueryCard
        v-model:proxy-mesh-url="proxyInput"
        v-model:debug-visible="debugVisible"
        v-model:bvh-visible="bvhVisible"
        v-model:bvh-depth="bvhDepth"
        v-model:gs-visible="gsVisible"
        title="空间运行时"
        subtitle="从一份代理网格构建表面、碰撞、导航表示，并把共享世界绑定到所有已加载的 3DGS 对象。"
        :badge="spatialBadge"
        :world-state="displayWorldState"
        :world-id="worldId"
        :binding-id="bindingId || '暂无绑定'"
        :binding-duration="bindingDurationLabel"
        :load-disabled="stats.totalModels === 0"
        :clear-disabled="!canClearSpatial"
        directory-pick-label="选择代理目录"
        :directory-entry-names="['proxy.glb', 'proxy.gltf', 'proxy.ply', 'scene.glb', 'scene.gltf', 'scene.ply']"
        :events="eventLog"
        @load="handleLoadProxyMesh()"
        @clear="clearProxyBinding()"
      />

      <SceneStatusCard
        title="运行时状态"
        subtitle="查看当前工具、视图模式、绑定对象和结果数量。"
        badge="空间"
        :items="statusItems"
        hint="测量查询优先命中测量表面；控制器查询优先命中相机控制表面。只有空间代理不可用时才回退到拾取。"
        action-label="停用当前工具"
        action-variant="subtle"
        :action-disabled="!activeTool"
        @action="deactivateTool()"
      />

      <SceneGroupCard
        title="联调工具"
        subtitle="在同一组面板里验证测量，以及表面、碰撞、导航三域运行时行为。"
        :badge="String(measureCount)"
      >
        <SceneMeasureCard
          title="测量"
          subtitle="直接通过新的表面查询接口驱动测量插件。"
          :options="MEASURE_OPTIONS"
          :active-label="activeMeasureDetail?.label ?? '距离'"
          :active-value="measureType"
          :active="activeTool === 'measure'"
          :clear-disabled="measureCount === 0"
          clear-label="清空测量结果"
          :note="measureNote"
          @select="handleMeasureSelection"
          @clear="clearMeasureResults()"
        />
      </SceneGroupCard>

      <SceneViewCard
        title="视图控制"
        subtitle="在代理空间世界持续绑定的情况下切换相机模式、控制器模式和预设视角。"
        :badge="cameraModeLabel"
        :is-perspective="cameraMode === CameraMode.Perspective"
        :options="VIEW_OPTIONS"
        :control-mode-options="CONTROL_MODE_OPTIONS"
        :active-control-mode="controlMode"
        :secondary-control-groups="
          controlMode === 'spacemouse' ? buildSpaceMouseSecondaryControlGroups(spaceMouseNavigation) : []
        "
        control-title="控制器"
        control-subtitle="检查所有控制器是否都解析到同一条空间表面路径。"
        fit-label="适配全部"
        @toggle-mode="toggleCameraMode()"
        @fit="fitAll()"
        @set-view="handleViewSelection"
        @set-control-mode="handleControlModeSelection"
        @set-secondary-control-group-mode="handleSpaceMouseSecondaryControlGroupSelection"
      />
    </SceneControlPanel>
  </div>
</template>

<style scoped>
.gs3d-page {
  --panel-gap: 0px;
  width: 100%;
  height: 100vh;
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(0, 3fr);
  gap: var(--panel-gap);
  padding: var(--panel-gap);
  box-sizing: border-box;
  align-items: stretch;
  background:
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.16), transparent 30%),
    linear-gradient(180deg, #020617 0%, #0f172a 100%);
}

.render-container {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  position: relative;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(2, 6, 23, 0.8);
}

@media (max-width: 1200px) {
  .gs3d-page {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(50vh, 1fr) auto;
  }
}
</style>
